import { ChevronDown, Tag } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LibrarySearchPicker } from "@/components/library/LibrarySearchPicker";
import { LibraryCategoryBadge } from "@/components/library/LibraryCategoryBadge";
import { WorkspaceChips } from "@/components/library/WorkspaceChips";
import {
  libraryItemStatusOptions,
  librarySourceTypeOptions,
  type LibraryItemStatus,
  type LibrarySourceType,
} from "@/lib/library";
import type { LibraryCategory } from "@/lib/libraryCategories";
import type { LibrarySortOption } from "@/components/library/myLibraryHelpers";

import type { Workspace } from "@/hooks/useWorkspace";

type Props = {
  librarySearchQuery: string;
  setLibrarySearchQuery: (v: string) => void;
  sortBy: LibrarySortOption;
  setSortBy: (v: LibrarySortOption) => void;
  libraryMode: "single" | "isolated" | "shared";
  workspaces: Workspace[];
  selectedWorkspaceIds: string[];
  toggleWorkspace: (id: string) => void;
  clearWorkspaces: () => void;
  selectedSourceTypes: LibrarySourceType[];
  setSelectedSourceTypes: (v: LibrarySourceType[]) => void;
  toggleSourceTypeFilter: (v: LibrarySourceType) => void;
  categories: LibraryCategory[];
  selectedCategoryIds: string[];
  setSelectedCategoryIds: (v: string[]) => void;
  toggleCategoryFilter: (id: string) => void;
  selectedStatusFilters: LibraryItemStatus[];
  setSelectedStatusFilters: (v: LibraryItemStatus[]) => void;
  toggleStatusFilter: (v: LibraryItemStatus) => void;
  showSelectionToggle: boolean;
  selectMode: boolean;
  onToggleSelectMode: () => void;
  filteredCount: number;
  filteredItemNoun: { singular: string; plural: string };
  hasActiveFilters: boolean;
};

/** All filter controls under the header: search, sort, workspace chips,
 *  source-type / category / status popovers, selection toggle, and the
 *  "Showing N items" footer. Pure presentational — owner page keeps state. */
export const MyLibraryFiltersBar = (p: Props) => (
  <div className="space-y-4">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0 flex-1 space-y-3">
        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_220px]">
          <LibrarySearchPicker value={p.librarySearchQuery} onChange={p.setLibrarySearchQuery} />
          <Select value={p.sortBy} onValueChange={(value) => p.setSortBy(value as LibrarySortOption)}>
            <SelectTrigger>
              <SelectValue placeholder="Sort sources" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Recently updated</SelectItem>
              <SelectItem value="title">Title A–Z</SelectItem>
              <SelectItem value="title-desc">Title Z–A</SelectItem>
              <SelectItem value="author">Author A–Z</SelectItem>
              <SelectItem value="author-desc">Author Z–A</SelectItem>
              <SelectItem value="published">Publication date (newest)</SelectItem>
              <SelectItem value="published-asc">Publication date (oldest)</SelectItem>
              <SelectItem value="status">Workflow status</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-3">
          {p.libraryMode === "shared" && p.workspaces.length > 1 ? (
            <WorkspaceChips
              workspaces={p.workspaces}
              selectedIds={p.selectedWorkspaceIds}
              onToggle={p.toggleWorkspace}
              onClear={p.clearWorkspaces}
            />
          ) : null}
          <div className="flex flex-wrap items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <span>Source type</span>
                  {p.selectedSourceTypes.length > 0 ? (
                    <span className="inline-flex items-center justify-center rounded-md bg-secondary px-1.5 py-0 text-[10px] font-semibold text-secondary-foreground">
                      {p.selectedSourceTypes.length}
                    </span>
                  ) : null}
                  <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-64 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-foreground">Filter by source type</p>
                  {p.selectedSourceTypes.length > 0 ? (
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => p.setSelectedSourceTypes([])}>
                      Clear
                    </Button>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-2">
                  {librarySourceTypeOptions.map((option) => (
                    <Button
                      key={option.value}
                      type="button"
                      variant={p.selectedSourceTypes.includes(option.value) ? "secondary" : "outline"}
                      size="sm"
                      onClick={() => p.toggleSourceTypeFilter(option.value)}
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <Tag className="h-4 w-4 text-primary" />
                  <span>Category filters</span>
                  {p.selectedCategoryIds.length > 0 ? (
                    <span className="inline-flex items-center justify-center rounded-md bg-secondary px-1.5 py-0 text-[10px] font-semibold text-secondary-foreground">
                      {p.selectedCategoryIds.length}
                    </span>
                  ) : null}
                  <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-72 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-foreground">Filter by category</p>
                  {p.selectedCategoryIds.length > 0 ? (
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => p.setSelectedCategoryIds([])}>
                      Clear
                    </Button>
                  ) : null}
                </div>
                {p.categories.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No categories yet.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {p.categories.map((category) => (
                      <LibraryCategoryBadge
                        key={category.id}
                        category={category}
                        selected={p.selectedCategoryIds.includes(category.id)}
                        onClick={() => p.toggleCategoryFilter(category.id)}
                      />
                    ))}
                  </div>
                )}
              </PopoverContent>
            </Popover>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <span>Status filters</span>
                  {p.selectedStatusFilters.length > 0 ? (
                    <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
                      {p.selectedStatusFilters.length}
                    </Badge>
                  ) : null}
                  <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-60 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-foreground">Filter by status</p>
                  {p.selectedStatusFilters.length > 0 ? (
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => p.setSelectedStatusFilters([])}>
                      Clear
                    </Button>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-2">
                  {libraryItemStatusOptions.map((option) => (
                    <Button
                      key={option.value}
                      type="button"
                      variant={p.selectedStatusFilters.includes(option.value) ? "secondary" : "outline"}
                      size="sm"
                      onClick={() => p.toggleStatusFilter(option.value)}
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            {p.showSelectionToggle ? (
              <Button variant="outline" size="sm" onClick={p.onToggleSelectMode}>
                {p.selectMode ? "Exit selection" : "Select sources"}
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
    <p className="text-sm text-muted-foreground">
      Showing {p.filteredCount} {p.filteredCount === 1 ? p.filteredItemNoun.singular : p.filteredItemNoun.plural}
      {p.hasActiveFilters ? " matching the current filters." : "."}
    </p>
  </div>
);
